import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiEdit } from 'react-icons/fi';
import ClientQuotationCategoriesPanel from './ClientQuotationCategoriesPanel';
import InlineLoading from './InlineLoading';
import useClientQuotationCategories from '../hooks/useClientQuotationCategories';
import { notify } from '../utils/alertBus';

const ClientQuotationCategoriesEditor = ({
  clientId,
  enabled,
  canEdit = false,
  compact = false,
  className = '',
}) => {
  const [editing, setEditing] = useState(false);
  const copy = {
    inactive: 'Portal de ordenes de compra inactivo.',
    title: 'Categorias para ordenes',
    description: 'Categorias visibles al crear ordenes de compra.',
    button: 'Editar categorias de ordenes',
    saved: 'Categorias de ordenes guardadas.',
    saveError: 'No se pudieron guardar las categorias de ordenes.',
    saveButton: 'Guardar categorias',
    empty: 'Sin categorias habilitadas',
    compactLabel: 'Ordenes',
    compactEmpty: 'Sin categorias para ordenes',
    panelDescription: 'Controla que categorias puede ver este cliente al crear ordenes de compra.',
    panelEmpty: 'Sin categorias seleccionadas: este cliente vera vacio el catalogo de ordenes de compra.',
  };
  const categories = useClientQuotationCategories(clientId, enabled);

  const selectedCategories = useMemo(
    () => categories.categoryOptions.filter((category) => categories.selectedCategoryIds.has(String(category.id))),
    [categories.categoryOptions, categories.selectedCategoryIds]
  );

  if (!enabled) {
    return compact ? null : (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 ${className}`}>
        {copy.inactive}
      </div>
    );
  }

  const handleSave = async () => {
    try {
      await categories.saveCategoryIds();
      setEditing(false);
      notify(copy.saved);
    } catch (error) {
      notify(copy.saveError, { type: 'error' });
    }
  };

  const handleCancel = async () => {
    setEditing(false);
    await categories.reload();
  };

  if (compact) {
    return (
      <div className={`rounded border border-gray-200 bg-white px-3 py-2 ${className}`}>
        <button
          type="button"
          onClick={() => canEdit && setEditing((prev) => !prev)}
          disabled={!canEdit}
          aria-expanded={editing}
          className="flex w-full items-start justify-between gap-2 text-left disabled:cursor-default"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
              {copy.compactLabel}
            </span>
            {categories.loading && !categories.loaded ? (
              <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">Cargando...</span>
            ) : categories.error ? (
              <span className="rounded border border-red-100 bg-red-50 px-2 py-1 text-xs text-red-700">
                Error al cargar categorias
              </span>
            ) : selectedCategories.length ? (
              selectedCategories.map((category) => (
                <span
                  key={category.id}
                  className="rounded border border-blue-100 bg-blue-50 px-2 py-1 text-xs text-blue-700"
                >
                  {category.name}
                </span>
              ))
            ) : (
              <span className="rounded border border-red-100 bg-red-50 px-2 py-1 text-xs text-red-700">
                {copy.compactEmpty}
              </span>
            )}
          </div>
          {canEdit && (
            <FiChevronDown
              className={`mt-1 shrink-0 text-gray-400 transition-transform ${editing ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          )}
        </button>

        {editing && (
          <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
            <ClientQuotationCategoriesPanel
              categoryOptions={categories.categoryOptions}
              selectedCategoryIds={categories.selectedCategoryIds}
              loading={categories.loading}
              saving={categories.saving}
              error={categories.error}
              disabled={!canEdit}
              title={copy.title}
              description={copy.panelDescription}
              emptyWarning={copy.panelEmpty}
              onToggle={categories.toggleCategory}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={categories.saving}
                className="rounded border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:text-gray-900 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={categories.saving || categories.loading}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {categories.saving ? 'Guardando...' : copy.saveButton}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-gray-200 bg-white p-4 ${className}`}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">{copy.title}</h3>
            <p className="text-xs text-gray-500">
              {copy.description}
            </p>
          </div>
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 rounded border border-blue-200 px-3 py-2 text-xs text-blue-700 hover:bg-blue-50"
            >
              <FiEdit />
              {copy.button}
            </button>
          )}
        </div>

        {categories.loading && !categories.loaded ? (
          <InlineLoading label="Cargando categorias" />
        ) : categories.error && !editing ? (
          <p className="text-xs text-red-600">{categories.error}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedCategories.length ? (
              selectedCategories.map((category) => (
                <span
                  key={category.id}
                  className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-700"
                >
                  {category.name}
                </span>
              ))
            ) : (
              <span className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">
                {copy.empty}
              </span>
            )}
          </div>
        )}

        {editing && (
          <div className="space-y-3">
            <ClientQuotationCategoriesPanel
              categoryOptions={categories.categoryOptions}
              selectedCategoryIds={categories.selectedCategoryIds}
              loading={categories.loading}
              saving={categories.saving}
              error={categories.error}
              disabled={!canEdit}
              title={copy.title}
              description={copy.panelDescription}
              emptyWarning={copy.panelEmpty}
              onToggle={categories.toggleCategory}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={categories.saving}
                className="rounded border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={categories.saving || categories.loading}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {categories.saving ? 'Guardando...' : copy.saveButton}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientQuotationCategoriesEditor;
